"use client";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import AppButton from "@/components/app-button";
import { AlertCircle } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations();
  const isDevelopment = process.env.NODE_ENV === "development";

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Alert className="mx-auto w-full max-w-lg border-destructive/30 bg-destructive/5 p-6">
        <div className="mb-2 flex items-center gap-2">
          <AlertCircle className="size-6 text-destructive" />
          <AlertTitle className="text-lg font-bold text-destructive">
            {t("notification.error.title")}
          </AlertTitle>
        </div>
        <AlertDescription className="mb-4 text-sm leading-6 text-muted-foreground">
          {t("notification.error.content")}
        </AlertDescription>

        {/* Raw error details are for developers only — never shown to end users */}
        {isDevelopment && (
          <pre className="mb-4 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-xs text-muted-foreground">
            {error.message}
          </pre>
        )}
        {error.digest && (
          <p className="mb-4 text-xs text-muted-foreground">#{error.digest}</p>
        )}

        <div className="flex flex-wrap gap-3">
          <AppButton variant="primary" onClick={() => reset()}>
            {t("notification.error.retry")}
          </AppButton>
          <AppButton variant="outline-secondary" asChild>
            <Link href="/">{t("notification.error.back-home")}</Link>
          </AppButton>
        </div>
      </Alert>
    </div>
  );
}
